import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Divider,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import CategoriesSelect from "../components/Categories/CategoriesSelect";

const toSlug = (text) =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9\-]/g, "");

const CategoriesPage = () => {
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const navigate = useNavigate();
  const { sectionSlug, categorySlug } = useParams();

  useEffect(() => {
    document.title = "Tienda | Hydroluz"; 
    window.scrollTo(0, 0);

    const fetchSections = async () => {
      try {
        const res = await fetch("/categorias/public/estructura/");
        if (!res.ok) throw new Error();
        const data = await res.json();
        setSections(data);

        if (sectionSlug && categorySlug) {
          const sec = data.find((s) => toSlug(s.name) === sectionSlug);
          const cat = sec?.categories.find(
            (c) => toSlug(c.name) === categorySlug
          );
          if (sec && cat) {
            setSelectedSection(sec);
            setSelectedCategory(cat);
          }
        } else {
          setSelectedSection(null);
          setSelectedCategory(null);
        }
      } catch (e) {
        console.error("Error al obtener secciones:", e);
      }
    };
    fetchSections();
  }, [sectionSlug, categorySlug]);

  const handleCategoryClick = (sec, cat) => {
    const sSlug = toSlug(sec.name);
    const cSlug = toSlug(cat.name);
    navigate(`/categorias/${sSlug}/${cSlug}`);
  };

  return (
    <Box sx={{ p: 4, bgcolor: "#f5f5f5" }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Typography
          variant="h1"
          align="center"
          fontWeight="bold"
          gutterBottom
          sx={{
            fontSize: { xs: "1.2rem", sm: "1.8rem", md: "2.5rem" },
            color: "#003366",
            textTransform: "uppercase",
            letterSpacing: "1px",
          }}
        >
          Productos Disponibles
        </Typography>
      </motion.div>

      {selectedCategory ? (
        <CategoriesSelect
          section={selectedSection}
          category={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />
      ) : (
        sections.length > 0 && (
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3, bgcolor: "#fff" }}>
            {sections.map((sec, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
              >
                <Box sx={{ mb: 4 }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontSize: { xs: "1rem", sm: "1.3rem", md: "1.5rem" },
                      fontWeight: "bold",
                      color: "#070707ff",
                      mb: 3,
                      textAlign: "left",
                    }}
                  >
                    {sec.name}
                  </Typography>

                  <Grid container spacing={2} alignItems="center">
                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{ display: "flex", justifyContent: "center" }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          width: { xs: "100%", sm: 260, md: 300 },
                          aspectRatio: "4/3",
                          p: 2,
                          borderRadius: 4,
                          overflow: "hidden",
                          background:
                            "linear-gradient(135deg, #e8ecf1 0%, #f6f8fa 100%)",
                          boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
                        }}
                      >
                        {sec.image && (
                          <Box
                            component="img"
                            src={sec.image}
                            alt={sec.name}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              filter:
                                "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                            }}
                          />
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={8}>
                      <Typography
                        align="center"
                        sx={{
                          fontWeight: "bold",
                          fontSize: { xs: "0.85rem", sm: "0.95rem" },
                          color: "#003366",
                          mb: 1,
                        }}
                      >
                        CATEGORÍAS
                      </Typography>
                      <Grid container spacing={1}>
                        {sec.categories.map((cat, idx) => (
                          <Grid item xs={6} sm={4} md={3} key={idx}>
                            <Box
                              onClick={() => handleCategoryClick(sec, cat)}
                              sx={{
                                textAlign: "center",
                                color: "#555",
                                cursor: "pointer",
                                fontSize: {
                                  xs: "0.8rem",
                                  sm: "0.85rem",
                                  md: "0.95rem",
                                },
                                py: 0.5,
                                "&:hover": {
                                  color: "#003366",
                                  fontWeight: "bold",
                                },
                              }}
                            >
                              {cat.name}
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  </Grid>

                  {i < sections.length - 1 && (
                    <Divider sx={{ my: 3, bgcolor: "#ddd" }} />
                  )}
                </Box>
              </motion.div>
            ))}
          </Paper>
        )
      )}
    </Box>
  );
};

export default CategoriesPage;
